import { defineStore } from 'pinia';
import { ref } from 'vue';
import useUserComposable from '@/composables/UserComposable';
import GlobalRequest from '@/api/global-request';
import { showToast } from '@/helpers/toast-handler';

const STEP_OUTCOME_URL = 'v2/template/steps';

export const useStepOutcomeStore = defineStore('stepOutcome', () => {
    const { company_id } = useUserComposable();
    const editOutcomeId = ref(null);
    const outcome = ref({
        type: 'default', // outcome type is only for front purpose
        company_id: null,
        formula_id: null,
        attribute_code: null,
        operator: null,
        value: null,
    });
    const outcomes = ref([]);
    // types is only for front easibility
    const types = ref([
        { id: 1, name: 'boolean', label: 'Boolean Formula' },
        { id: 2, name: 'value', label: 'Value Based Formula' },
        { id: 3, name: 'attribute', label: 'Attribute Based' },
    ]);
    const operators = ref([
        { label: 'Equals to', value: '==' },
        { label: 'Less than', value: '<' },
        { label: 'Less than or Equals to', value: '<=' },
        { label: 'Greater than', value: '>' },
        { label: 'Greater than or Equals to', value: '>=' },
    ]);

    // helpers
    const initOutcome = () => {
        outcome.value.type = 'default'; // outcome type is only for front purpose
        outcome.value.company_id = null;
        outcome.value.formula_id = null;
        outcome.value.attribute_code = null;
        outcome.value.operator = null;
        outcome.value.value = null;
    };

    // this is for initial Editing Pass configuration when assessment details are extracted and loaded into the assessment builder UI
    // this function usually takes the outcomes array from the step store and will populate them into the outcome states
    const populateStepOutcomes = (data) => {
        // Initially clean up the existing outcomes before populating it
        outcomes.value = [];
        data?.forEach((outcome) => {
            const outcomeObj = {
                id: outcome?.id,
                company_id: company_id.value,
                formula_id: outcome.formula_id,
                attribute_code: outcome.attribute_code,
                operator: outcome.operator,
                value: outcome.value,
            };
            outcomes.value?.push(outcomeObj);
        });
    };

    // actions
    const createOutcome = async (body, stepId) => {
        try {
            const response = await GlobalRequest.postRequest(`${STEP_OUTCOME_URL}/${stepId}/pass`, { ...body, company_id: company_id.value });
            if (response.statusCode === 201) {
                showToast(response, 'Pass criteria added successfully');
                // push the new outcome created into the outcomes array
                outcomes.value.push(response.data);
                return true;
            }
            return false;
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to add pass criteria');
        }
    };

    // setup outcome details into stepOutcomeStore outcome for edit
    const editOutcome = async (data) => {
        // populate all the values from the data
        editOutcomeId.value = data.id;
        outcome.value = {
            company_id: company_id.value,
            formula_id: data.formula_id,
            attribute_code: data.attribute_code,
            operator: data.operator,
            value: parseInt(data.value),
        };
        // setup the value attribute type for front easibility
        if (data.attribute_code) {
            outcome.value.type = 'attribute';
        } else if (data.formula_id) {
            if (data.formula?.type == 1) {
                outcome.value.type = 'boolean';
            } else {
                outcome.value.type = 'value';
            }
        } else {
            outcome.value.type = null;
        }
    };

    // need to put step id as well as pass_fail_id here later
    const updateOutcome = async (data, stepId, outcomeId) => {
        try {
            const editId = outcomeId ?? editOutcomeId?.value;
            const response = await GlobalRequest.patchRequest(`${STEP_OUTCOME_URL}/${stepId}/pass/${editId}`, { ...data, company_id: company_id.value });
            if (response.statusCode === 200) {
                showToast(response, 'Pass criteria updated successfully');
                // need to update that particular outcome object in the whole outcomes array here
                // find index first
                const existingOutcomeIndex = outcomes.value.findIndex((outcome) => outcome.id === editOutcomeId.value);
                // now if that outcome exists then update that outcome
                if (existingOutcomeIndex !== -1) {
                    const updatedOutcomeObj = {
                        id: response?.data?.id,
                        company_id: company_id.value,
                        formula_id: response?.data?.formula_id,
                        attribute_code: response?.data?.attribute_code,
                        operator: response?.data?.operator,
                        value: response?.data?.value,
                    };
                    // now replace that index with the new one
                    outcomes.value[existingOutcomeIndex] = updatedOutcomeObj;
                }
                return true;
            }
            return false;
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to update pass criteria');
        }
    };

    const deleteOutcome = async (stepId, outcomeId) => {
        try {
            const response = await GlobalRequest.deleteRequest(`${STEP_OUTCOME_URL}/${stepId}/pass/${outcomeId}`, {
                params: { company_id: company_id.value },
            });
            if (response.statusCode === 200) {
                showToast(response, 'Pass criteria removed');
                outcomes.value = outcomes.value.filter((outcome) => outcome.id !== outcomeId);
            }
        } catch (error) {
            showToast({}, error?.data?.message ?? 'Failed to remove pass criteria');
        }
    };

    return {
        // states
        editOutcomeId,
        outcome,
        outcomes,
        types,
        operators,
        // getters

        // actions
        createOutcome,
        editOutcome,
        updateOutcome,
        deleteOutcome,
        // helpers
        initOutcome,
        populateStepOutcomes,
    };
});
